import React, { useRef, useState, useImperativeHandle } from 'react';
import { IconButton, Tooltip, Snackbar } from '@mui/material';
import Alert from '@mui/material/Alert';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { attachFiles } from '../../twilio/store/genericActions';
import type { SxProps, Theme } from '@mui/material/styles';

const MAX_FILE_SIZE_BYTES = 4 * 1024 * 1024; // 1MB

export interface AttachFileButtonRef {
  showSuccess: (msg: string) => void;
}

interface AttachFileButtonProps {
  accept?: string;
  multiple?: boolean;
  buttonSx?: SxProps<Theme>;
}

const AttachFileButton = React.forwardRef<AttachFileButtonRef, AttachFileButtonProps>(
  ({ accept = 'image/*,application/pdf', multiple = true, buttonSx }, ref) => {
    const attachmentButtonStyles = {
      position: 'absolute' as const,
      zIndex: 2,
      right: '63px',
      top: '22px',
    };

    const inputRef = useRef<HTMLInputElement>(null);
    const dispatch = useDispatch();
    const [snackbarMsg, setSnackbarMsg] = useState<string | null>(null);
    const [snackOpen, setSnackOpen] = useState(false);
    const [isValid, setIsValid] = useState<boolean>();
    const onPick = () => inputRef.current?.click();

    const configState = useSelector((state: RootState) => state.config);
    const config = configState.config;
    const formData = config?.data?.[0]?.attributes;

    const onChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
      const files = Array.from(e.target.files || []);
      if (!files.length) return;

      const allowedExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg', '.pdf'];
      const unsupportedFiles = files.filter((f) => {
        const extension = f.name.toLowerCase().substring(f.name.lastIndexOf('.'));
        return !allowedExtensions.includes(extension);
      });
      const tooLarge = files.filter((f) => f.size > MAX_FILE_SIZE_BYTES);
      const valid = files.filter((f) => {
        const extension = f.name.toLowerCase().substring(f.name.lastIndexOf('.'));
        return allowedExtensions.includes(extension) && f.size <= MAX_FILE_SIZE_BYTES;
      });

      if (unsupportedFiles.length) {
        const names = unsupportedFiles.map((f) => f.name).join(', ');
        setIsValid(false);
        setSnackbarMsg(`Unsupported file type(s): ${names}`);
        setSnackOpen(true);
      }

      if (tooLarge.length) {
        const names = tooLarge.map((f) => f.name).join(', ');
        setIsValid(false);
        setSnackbarMsg(`${formData?.ChatBox?.FileSizeErrorMessage || 'File size exceeds 1MB'}: ${names}`);
        setSnackOpen(true);
      }

      if (valid.length) {
        dispatch(attachFiles({ filesToAttach: valid as File[] }));
        setIsValid(true);
      }

      if (inputRef.current) inputRef.current.value = '';
    };

    const handleClose = () => setSnackOpen(false);

    useImperativeHandle(ref, () => ({
      showSuccess: (msg: string) => {
        setIsValid(true);
        setSnackbarMsg(msg);
        setSnackOpen(true);
      },
    }));

    return (
      <>
        <input ref={inputRef} type="file" accept={accept} multiple={multiple} hidden onChange={onChange} />
        <Tooltip title="Attach files">
          <IconButton
            sx={{
              ...attachmentButtonStyles,
              ...(buttonSx as object || {}),
            }}
            aria-label="attach"
            onClick={onPick}
            size="small"
          >
            <AttachFileIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Snackbar
          open={snackOpen}
          autoHideDuration={4000}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={handleClose} severity={isValid ? 'success' : 'error'} variant="filled" sx={{ width: '100%' }}>
            {snackbarMsg}
          </Alert>
        </Snackbar>
      </>
    );
  }
);

export default AttachFileButton;
